<template>
  <div class="upload-container">
    <MainNavbar />

    <router-link :to="{ name: 'TournamentLanding' }" class="back-to-dashboard">
      <i class="fa-solid fa-arrow-left" aria-hidden="true" />
      Back to Tournaments
    </router-link>

    <h1 class="page-title">
      <i class="fa-solid fa-trophy" aria-hidden="true" />
      Create Tournament
    </h1>

    <div class="upload-card fadeIn">
      <div class="steps-wrapper">
        <div class="progress-line">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
        </div>
        <div class="steps">
          <div
            v-for="s in steps"
            :key="s.id"
            class="step"
            :class="{ active: step === s.id, completed: step > s.id }"
          >
            <i :class="s.icon" aria-hidden="true" />
            <span class="step-number">Step {{ s.id }}</span>
            <div class="step-label">{{ s.label }}</div>
          </div>
        </div>
      </div>

      <!-- STEP 1 — TOURNAMENT DETAILS -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 1" key="step1" class="step-content">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-futbol" aria-hidden="true" /> Tournament Details</h2>
            <p class="step-description">Host, teams, format, and competition type</p>
          </div>

          <div class="form-grid">
            <div class="form-column">
              <div class="form-group">
                <label>
                  <i class="fa-solid fa-building" aria-hidden="true" />
                  Host team / Host organization
                </label>
                <input type="text" :value="hostName" readonly class="readonly-input" />
                <p class="input-hint">Locked to your logged-in IPA customer account.</p>
              </div>

              <div class="form-group">
                <label>
                  <i class="fa-solid fa-users" aria-hidden="true" />
                  Number of team participants *
                </label>
                <input
                  v-model.number="form.participantTeams"
                  type="number"
                  min="8"
                  step="2"
                />
                <p class="input-hint">
                  Minimum 8 teams (even number only). Includes the host team. This count sets how
                  many matches the computerized scheduler must place in step 2.
                </p>
              </div>

              <div class="form-group">
                <label>
                  <i class="fa-solid fa-child-reaching" aria-hidden="true" />
                  Age bracket *
                </label>
                <select v-model="form.ageBracket">
                  <option v-for="opt in ageBrackets" :key="opt" :value="opt">{{ opt }}</option>
                </select>
              </div>
            </div>

            <div class="form-column">
              <div class="form-group">
                <label>
                  <i class="fa-solid fa-shirt" aria-hidden="true" />
                  Infield + keeper player limit *
                </label>
                <select v-model="form.playerFormat">
                  <option v-for="opt in playerFormats" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label>
                  <i class="fa-solid fa-sitemap" aria-hidden="true" />
                  Game type *
                </label>
                <select v-model="form.gameType">
                  <option v-for="opt in gameTypes" :key="opt" :value="opt">{{ opt }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="full-width-fields">
            <div class="form-group">
              <label for="team-category">
                <i class="fa-solid fa-layer-group" aria-hidden="true" />
                Team category *
              </label>
              <select id="team-category" v-model="form.teamCategory">
                <option value="" disabled>Select one category</option>
                <option v-for="opt in teamCategoryOptions" :key="opt" :value="opt">{{ opt }}</option>
              </select>
              <p class="input-hint">Choose the single category that best describes participating teams.</p>
            </div>
          </div>

          <div class="step-navigation">
            <div class="step-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: step1Progress + '%' }" />
              </div>
              <span class="progress-text">{{ step1Progress }}% Complete</span>
            </div>
            <button type="button" class="next-btn animated" @click="validateStep1">
              Continue to Schedule
              <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </transition>

      <!-- STEP 2 — SCHEDULE & VENUE -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 2" key="step2" class="step-content">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-calendar-days" aria-hidden="true" /> Schedule &amp; Venue</h2>
            <p class="step-description">Dates, fields, match duration, and team arrival</p>
          </div>

          <div class="form-grid">
            <div class="form-column">
              <div class="form-group">
                <label>
                  <i class="fa-solid fa-calendar" aria-hidden="true" />
                  Tournament date mode *
                </label>
                <select v-model="form.dateMode">
                  <option value="single">Single day</option>
                  <option value="multiple">Multiple days</option>
                </select>
              </div>

              <div v-if="form.dateMode === 'single'" class="form-group">
                <label>Match day *</label>
                <input v-model="form.singleDate" type="date" />
              </div>

              <template v-else>
                <div class="form-group">
                  <label>Start date *</label>
                  <input v-model="form.startDate" type="date" @change="onStartDateChange" />
                </div>
                <div class="form-group">
                  <label>End date *</label>
                  <input
                    v-model="form.endDate"
                    type="date"
                    :min="form.startDate || undefined"
                    :class="{ 'input-invalid': dateRangeInvalid }"
                  />
                  <p v-if="dateRangeInvalid" class="field-error">
                    End date cannot be before the start date.
                  </p>
                </div>
              </template>

              <div class="form-group">
                <label>
                  <i class="fa-solid fa-clock" aria-hidden="true" />
                  Team arrival time *
                </label>
                <input v-model="form.teamArrivalTime" type="time" />
                <p v-if="schedulingCapacityGuide.firstKickoffTime !== '—'" class="input-hint">
                  First computerized kickoff:
                  <strong>{{ schedulingCapacityGuide.firstKickoffTime }}</strong>
                  (arrival + {{ FIRST_MATCH_AFTER_ARRIVAL_MINUTES }} min)
                </p>
              </div>
            </div>

            <div class="form-column">
              <div class="form-group">
                <label>
                  <i class="fa-solid fa-map-location-dot" aria-hidden="true" />
                  Venue setup *
                </label>
                <select v-model="form.venueFieldMode" @change="onVenueModeChange">
                  <option value="single">Single field</option>
                  <option value="multiple">Multiple fields</option>
                </select>
              </div>

              <div v-if="form.venueFieldMode === 'multiple'" class="form-group">
                <label>How many fields? *</label>
                <select v-model.number="form.fieldSlotCount" @change="syncVenueSlots">
                  <option :value="2">2 fields</option>
                  <option :value="3">3 fields</option>
                </select>
                <p class="input-hint">Maximum of 3 fields per tournament.</p>
              </div>

              <div class="form-group">
                <label>
                  <i class="fa-solid fa-stopwatch" aria-hidden="true" />
                  Duration per match (minutes) *
                </label>
                <input v-model.number="form.matchDurationMinutes" type="range" min="15" max="120" step="5" />
                <p class="input-hint">
                  Minimum 15 minutes · currently {{ form.matchDurationMinutes }} min ·
                  {{ schedulingCapacityGuide.slotMinutes }} min per slot (match +
                  {{ FIXTURE_GAP_MINUTES }} min gap)
                </p>
              </div>
            </div>
          </div>

          <section
            class="schedule-capacity-guide"
            :class="`schedule-capacity-guide--${schedulingCapacityGuide.status}`"
            aria-labelledby="schedule-capacity-heading"
          >
            <div class="schedule-capacity-guide__head">
              <h3 id="schedule-capacity-heading" class="schedule-capacity-guide__title">
                <i class="fa-solid fa-calculator" aria-hidden="true" />
                Computerized fixture capacity
              </h3>
              <p class="schedule-capacity-guide__lead">
                Plan days and fields so the random draw scheduler can fit every match before
                {{ schedulingCapacityGuide.dayEndTime }} each day.
              </p>
            </div>

            <p class="schedule-capacity-guide__headline">
              <i
                :class="schedulingCapacityIcon"
                aria-hidden="true"
              />
              {{ schedulingCapacityGuide.headline }}
            </p>

            <div class="schedule-capacity-guide__grid">
              <div class="schedule-capacity-stat">
                <span class="schedule-capacity-stat__label">Teams (step 1)</span>
                <strong>{{ schedulingCapacityGuide.teams || "—" }}</strong>
              </div>
              <div class="schedule-capacity-stat">
                <span class="schedule-capacity-stat__label">Format</span>
                <strong>{{ schedulingCapacityGuide.formatLabel || "—" }}</strong>
              </div>
              <div class="schedule-capacity-stat">
                <span class="schedule-capacity-stat__label">Fixtures needed</span>
                <strong>{{ schedulingCapacityGuide.requiredMatches || "—" }}</strong>
              </div>
              <div class="schedule-capacity-stat">
                <span class="schedule-capacity-stat__label">Tournament days</span>
                <strong>{{ schedulingCapacityGuide.days || "—" }}</strong>
              </div>
              <div class="schedule-capacity-stat">
                <span class="schedule-capacity-stat__label">Fields</span>
                <strong>{{ schedulingCapacityGuide.fields || "—" }}</strong>
              </div>
              <div class="schedule-capacity-stat">
                <span class="schedule-capacity-stat__label">Slots / field / day</span>
                <strong>{{ schedulingCapacityGuide.perFieldPerDay || "—" }}</strong>
              </div>
              <div class="schedule-capacity-stat schedule-capacity-stat--highlight">
                <span class="schedule-capacity-stat__label">Total slots available</span>
                <strong>{{ schedulingCapacityGuide.totalCapacity || "—" }}</strong>
              </div>
              <div class="schedule-capacity-stat">
                <span class="schedule-capacity-stat__label">First kickoff</span>
                <strong>{{ schedulingCapacityGuide.firstKickoffTime }}</strong>
              </div>
            </div>

            <ul class="schedule-capacity-guide__rules">
              <li>
                <i class="fa-solid fa-hourglass-start" aria-hidden="true" />
                First match starts {{ FIRST_MATCH_AFTER_ARRIVAL_MINUTES }} minutes after team
                arrival
              </li>
              <li>
                <i class="fa-solid fa-stopwatch" aria-hidden="true" />
                {{ FIXTURE_GAP_MINUTES }} minutes gap between fixtures on the same field
              </li>
              <li>
                <i class="fa-solid fa-clock" aria-hidden="true" />
                Play window ends at {{ schedulingCapacityGuide.dayEndTime }} daily
              </li>
            </ul>

            <ul
              v-if="schedulingCapacityGuide.suggestions.length"
              class="schedule-capacity-guide__suggestions"
            >
              <li v-for="(tip, idx) in schedulingCapacityGuide.suggestions" :key="idx">
                {{ tip }}
              </li>
            </ul>
          </section>

          <div class="full-width-fields venue-fields-block">
            <h3 class="venue-fields-title">
              <i class="fa-solid fa-location-dot" aria-hidden="true" />
              Field pin locations
            </h3>
            <p class="input-hint venue-fields-lead">
              Add a pin for each field, or book from IPA registered facilities using the button below.
            </p>

            <div
              v-for="(_, idx) in venueSlotCount"
              :key="`pin-slot-${idx}`"
              class="venue-slot"
            >
              <div class="form-group">
                <label :for="`pin-slot-${idx}`">
                  {{ venueSlotCount > 1 ? `Field ${idx + 1} pin location` : "Pin location" }}
                </label>
                <input
                  :id="`pin-slot-${idx}`"
                  v-model.trim="form.pinSlots[idx]"
                  type="text"
                  placeholder="Paste Google Maps pin or location link"
                />
              </div>
              <div v-if="facilityBookings[idx]" class="booked-facility">
                <i class="fa-solid fa-circle-check" aria-hidden="true" />
                <div>
                  <strong>{{ facilityBookings[idx].court.pitch_name }}</strong>
                  <span v-if="facilityBookings[idx].booking?.booking_number">
                    · #{{ facilityBookings[idx].booking.booking_number }}
                  </span>
                </div>
                <button type="button" class="link-btn" @click="clearFacilityBooking(idx)">Change</button>
              </div>
            </div>

            <button type="button" class="book-facility-btn" @click="openFacilityBrowse">
              <i class="fa-solid fa-building" aria-hidden="true" />
              {{
                form.venueFieldMode === "multiple"
                  ? "Book multiple fields"
                  : "Book from registered facilities"
              }}
            </button>

            <NeutralCourtPicker
              facility-type="football"
              :booked="null"
              :required="false"
              :auto-open-browse="facilityBrowseOpen"
              :suggested-date="suggestedVenueDate"
              :suggested-start-time="form.teamArrivalTime"
              :suggested-duration-minutes="venueBookingDurationMinutes"
              class="facility-picker-silent"
              @update:booked="onFacilityBooked"
              @browse-closed="facilityBrowseOpen = false"
            />
          </div>

          <div class="nav-buttons">
            <button type="button" class="nav-btn secondary" @click="goBack">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" />
              Back to Details
            </button>
            <button type="button" class="nav-btn primary animated" @click="validateStep2">
              Continue to Officials
              <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </transition>

      <!-- STEP 3 — OFFICIALS -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 3" key="step3" class="step-content">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-user-shield" aria-hidden="true" /> Match Officials</h2>
            <p class="step-description">Football officiating slots and sourcing</p>
          </div>

          <div class="instruction-card">
            <i class="fa-solid fa-scale-balanced" aria-hidden="true" />
            <div>
              <h4>Match officials (football)</h4>
              <p>
                Mandatory for game quality. Performance data (goals, assists) is collected by independent
                officials or IPA staff. Private officials must be declared; otherwise accounts may be suspended.
              </p>
            </div>
          </div>

          <div class="form-group">
            <label>Official types required *</label>
            <div class="chip-wrap">
              <button
                v-for="type in officialTypeOptions"
                :key="type"
                type="button"
                class="chip"
                :class="{ active: form.officialTypes.includes(type) }"
                @click="toggleOfficialType(type)"
              >
                {{ type }}
              </button>
            </div>
          </div>

          <div v-if="form.officialSlots.length" class="slots-grid">
            <div v-for="slot in form.officialSlots" :key="slot.type" class="form-group">
              <label>{{ slot.type }} slots</label>
              <input v-model.number="slot.count" type="number" min="1" />
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label>Officials sourcing *</label>
              <select v-model="form.officialAssignmentMode">
                <option value="platform">Allow registered officials to claim slots</option>
                <option value="private">Assign privately owned officials</option>
              </select>
            </div>
            <div class="form-group">
              <label>Officials budget (KSH)</label>
              <input v-model.number="form.officialsBudget" type="number" min="0" />
            </div>
            <div v-if="form.officialAssignmentMode === 'private'" class="form-group form-group--full">
              <label>Private official details *</label>
              <textarea
                v-model.trim="form.privateOfficialNotes"
                rows="2"
                placeholder="Names, contacts, and roles"
              />
            </div>
          </div>

          <div class="nav-buttons">
            <button type="button" class="nav-btn secondary" @click="goBack">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" />
              Back to Schedule
            </button>
            <button type="button" class="nav-btn primary animated" @click="validateStep3">
              Continue to Services
              <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </transition>

      <!-- STEP 4 — SERVICE PROVIDERS -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 4" key="step4" class="step-content">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-briefcase" aria-hidden="true" /> Service Providers</h2>
            <p class="step-description">Budget sound, food, and emergency support before prize planning</p>
          </div>

          <div class="instruction-card">
            <i class="fa-solid fa-wallet" aria-hidden="true" />
            <div>
              <h4>Budget these before prizes</h4>
              <p>
                Contributed tournament money will first cater for service providers and match officials.
                Prize awards are calculated from what remains after these costs.
              </p>
            </div>
          </div>

          <div class="earnings-breakdown">
            <p class="input-hint" style="margin-bottom: 0.75rem">
              Food vendors avail themselves at their own cost. Organisers budget sound system and emergency services.
            </p>
            <div class="form-grid">
              <div class="form-group toggle-row">
                <label>
                  <input v-model="form.serviceProviders.sound.enabled" type="checkbox" />
                  Sound system
                </label>
                <input
                  v-if="form.serviceProviders.sound.enabled"
                  v-model.number="form.serviceProviders.sound.slots"
                  type="number"
                  min="1"
                  placeholder="Slots"
                />
              </div>
              <div class="form-group toggle-row food-open-market-row">
                <label>
                  <input v-model="form.serviceProviders.food.enabled" type="checkbox" />
                  Food services
                </label>
                <p v-if="form.serviceProviders.food.enabled" class="food-open-market-note">
                  Open market — vendors can sell freely; no slot limit and automatically marked as
                  available on your dashboard.
                </p>
              </div>
              <div class="form-group toggle-row">
                <label>
                  <input v-model="form.serviceProviders.emergency.enabled" type="checkbox" />
                  Emergency services
                </label>
                <input
                  v-if="form.serviceProviders.emergency.enabled"
                  v-model.number="form.serviceProviders.emergency.slots"
                  type="number"
                  min="1"
                  placeholder="Slots"
                />
              </div>
              <div class="form-group">
                <label>Sound system budget (KSH)</label>
                <input
                  v-model.number="form.soundBudget"
                  type="number"
                  min="0"
                  :disabled="!form.serviceProviders.sound.enabled"
                />
              </div>
              <div class="form-group">
                <label>Emergency services budget (KSH)</label>
                <input
                  v-model.number="form.emergencyBudget"
                  type="number"
                  min="0"
                  :disabled="!form.serviceProviders.emergency.enabled"
                />
              </div>
            </div>
            <div class="budget-summary">
              <span>Estimated operational cost so far</span>
              <strong>{{ currency(operationalBudget) }}</strong>
            </div>
          </div>

          <div class="nav-buttons">
            <button type="button" class="nav-btn secondary" @click="goBack">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" />
              Back to Officials
            </button>
            <button type="button" class="nav-btn primary animated" @click="validateStep4">
              Continue to Prizes
              <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </transition>

      <!-- STEP 5 — PRIZE MONEY & AWARDS -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 5" key="step5" class="step-content">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-coins" aria-hidden="true" /> Prize Money &amp; Awards</h2>
            <p class="step-description">Set contributions and winner payouts after service costs</p>
          </div>

          <div class="instruction-card">
            <i class="fa-solid fa-circle-info" aria-hidden="true" />
            <div>
              <h4>How contributions are used</h4>
              <p>
                Money contributed by the host or registered teams will first cover service providers
                (sound system, emergency services) and match officials. Remaining funds can be allocated
                as cash awards or trophies.
              </p>
            </div>
          </div>

          <div class="pricing-form">
            <div class="form-group">
              <label>
                <i class="fa-solid fa-hand-holding-dollar" aria-hidden="true" />
                Contribution model *
              </label>
              <select v-model="form.prizeContributionModel">
                <option value="host">Host contribution (full)</option>
                <option value="teams">Registered teams contribution</option>
              </select>
            </div>

            <div v-if="form.prizeContributionModel === 'host'" class="form-group">
              <label>Host contribution (KSH) *</label>
              <input v-model.number="form.hostPrizeContribution" type="number" min="0" />
            </div>

            <template v-else>
              <div class="form-group">
                <label>Total prize pool target (KSH) *</label>
                <input v-model.number="form.teamContributionTotal" type="number" min="0" />
              </div>
              <div class="instruction-card">
                <i class="fa-solid fa-calculator" aria-hidden="true" />
                <div>
                  <h4>Per-team contribution</h4>
                  <p>{{ currency(perTeamContribution) }} per team ({{ form.participantTeams }} teams)</p>
                </div>
              </div>
            </template>

            <div class="earnings-breakdown">
              <h4 class="pricing-section-heading">
                <i class="fa-solid fa-receipt" aria-hidden="true" />
                Budget breakdown
              </h4>
              <ul class="budget-lines">
                <li><span>Total contributed</span><strong>{{ currency(totalPrizePool) }}</strong></li>
                <li><span>Service providers &amp; officials</span><strong>- {{ currency(operationalBudget) }}</strong></li>
                <li class="budget-lines__remain">
                  <span>Available for awards</span><strong>{{ currency(availableAwardBudget) }}</strong>
                </li>
              </ul>

              <h4 class="pricing-section-heading" style="margin-top: 1.25rem">
                <i class="fa-solid fa-award" aria-hidden="true" />
                Awards plan
              </h4>
              <div class="chip-wrap">
                <button
                  v-for="mode in awardModes"
                  :key="mode"
                  type="button"
                  class="chip"
                  :class="{ active: form.awardModes.includes(mode) }"
                  @click="toggleMulti(form.awardModes, mode)"
                >
                  {{ mode }}
                </button>
              </div>
              <div class="form-grid" style="margin-top: 1rem">
                <div class="form-group">
                  <label>Award distribution</label>
                  <select v-model="form.awardSplit">
                    <option value="first-only">First place only</option>
                    <option value="first-second">First and second place</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>First place (KSH)</label>
                  <input v-model.number="form.firstPlaceAward" type="number" min="0" />
                </div>
                <div v-if="form.awardSplit === 'first-second'" class="form-group">
                  <label>Second place (KSH)</label>
                  <input v-model.number="form.secondPlaceAward" type="number" min="0" />
                </div>
              </div>
              <p v-if="awardOverBudget" class="inline-alert">
                <i class="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                Awards exceed available budget after service providers and officials.
              </p>
            </div>

            <h4 class="pricing-section-heading" style="margin-top: 1.5rem">
              <i class="fa-solid fa-building-columns" aria-hidden="true" />
              IPA Finance &amp; payments
            </h4>

            <div class="instruction-card">
              <i class="fa-solid fa-shield-halved" aria-hidden="true" />
              <div>
                <h4>Prize funds in trust</h4>
                <p>
                  IPA Finance can hold contributed prize money in trust and return or issue payouts
                  to winners after the tournament concludes.
                </p>
              </div>
            </div>

            <div class="form-group">
              <label>Where should prize contributions be paid? *</label>
              <select v-model="form.paymentCollectionMode" @change="onPaymentModeChange">
                <option value="ipa_paybill">IPA Paybill (M-Pesa 522522)</option>
                <option value="host_direct">Directly to my account</option>
              </select>
            </div>

            <template v-if="form.paymentCollectionMode === 'ipa_paybill'">
              <div class="form-group">
                <label>IPA Paybill number</label>
                <input
                  type="text"
                  class="readonly-input"
                  :value="IPA_TOURNAMENT_PAYBILL"
                  readonly
                  aria-readonly="true"
                />
              </div>
              <div class="instruction-card">
                <i class="fa-solid fa-users" aria-hidden="true" />
                <div>
                  <h4>Account number (per team)</h4>
                  <p>
                    You do not set an account number when creating the tournament. When teams
                    contribute, each team pays using their registered <strong>team name</strong> as
                    the Paybill account number.
                  </p>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="form-group">
                <label>Payment option *</label>
                <select v-model="form.hostPaymentProvider">
                  <option disabled value="">Select provider</option>
                  <option value="mpesa">M-Pesa</option>
                  <option value="airtel_money">Airtel Money</option>
                </select>
              </div>
              <div v-if="form.hostPaymentProvider" class="form-group">
                <label>
                  {{
                    form.hostPaymentProvider === "mpesa"
                      ? "M-Pesa phone or Paybill number *"
                      : "Airtel Money phone or Paybill number *"
                  }}
                </label>
                <input
                  v-model.trim="form.hostPaymentDestination"
                  type="text"
                  maxlength="64"
                  :placeholder="
                    form.hostPaymentProvider === 'mpesa'
                      ? 'e.g. 254712345678 or your Paybill'
                      : 'e.g. 254712345678'
                  "
                />
                <p class="input-hint">
                  Enter the phone number or Paybill where teams should send prize contributions.
                </p>
              </div>
            </template>
          </div>

          <div class="nav-buttons">
            <button type="button" class="nav-btn secondary" @click="goBack">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" />
              Back to Services
            </button>
            <button type="button" class="nav-btn primary animated" @click="validateStep5">
              Continue to Poster
              <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </transition>

      <!-- STEP 6 — POSTER & REVIEW -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 6" key="step6" class="step-content">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-image" aria-hidden="true" /> Poster &amp; Review</h2>
            <p class="step-description">Upload your fanbase poster and confirm setup</p>
          </div>

          <div class="upload-instructions">
            <div class="instruction-card">
              <i class="fa-solid fa-bullhorn" aria-hidden="true" />
              <div>
                <h4>Fanbase poster</h4>
                <p>Displayed in the IPA community so teams can discover and register.</p>
              </div>
            </div>
            <div class="instruction-card">
              <i class="fa-solid fa-shirt" aria-hidden="true" />
              <div>
                <h4>Jerseys</h4>
                <p>Teams are advised to wear official jerseys for better game quality.</p>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>
              <i class="fa-solid fa-file-image" aria-hidden="true" />
              Tournament poster *
            </label>
            <input type="file" accept="image/*" @change="onPosterChange" />
          </div>

          <div v-if="posterPreview" class="poster-preview">
            <img :src="posterPreview" alt="Tournament poster preview" />
          </div>

          <div class="review-panel">
            <h4 class="pricing-section-heading">
              <i class="fa-solid fa-list-check" aria-hidden="true" />
              Registration summary
            </h4>
            <ul class="review-list">
              <li><strong>Host:</strong> {{ hostName }}</li>
              <li><strong>Teams:</strong> {{ form.participantTeams }} · {{ selectedPlayerFormatLabel }}</li>
              <li><strong>Age:</strong> {{ form.ageBracket }} · <strong>Game:</strong> {{ form.gameType }}</li>
              <li><strong>Team category:</strong> {{ form.teamCategory || "—" }}</li>
              <li><strong>Dates:</strong> {{ dateSummary }}</li>
              <li><strong>Venue:</strong> {{ venueSummary }}</li>
              <li><strong>Arrival:</strong> {{ form.teamArrivalTime || "—" }} · {{ form.matchDurationMinutes }} min/match</li>
              <li><strong>Prize pool:</strong> {{ currency(totalPrizePool) }}</li>
              <li><strong>Payments:</strong> {{ paymentSummary }}</li>
              <li><strong>Official slots:</strong> {{ totalOfficialSlots }}</li>
              <li><strong>Award budget:</strong> {{ currency(availableAwardBudget) }}</li>
            </ul>
            <p class="rule-note">
              <i class="fa-solid fa-gavel" aria-hidden="true" />
              Rules of the game are determined by IPA.
            </p>
          </div>

          <div class="nav-buttons">
            <button type="button" class="nav-btn secondary" @click="goBack">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" />
              Back to Prizes
            </button>
            <button
              type="button"
              class="nav-btn primary animated"
              :disabled="submitting"
              @click="submitRegistration"
            >
              <i class="fa-solid fa-check-circle" aria-hidden="true" />
              {{ submitting ? "Saving…" : "Complete registration setup" }}
            </button>
          </div>
        </div>
      </transition>
    </div>

    <div class="upload-footer-wrap">
      <MainFooter />
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/authen/auth";
import { buildApiUrl } from "@/config/api";
import { dispatchAppToast } from "@/utils/toast";
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import NeutralCourtPicker from "@/routes/match_hub/shared/NeutralCourtPicker.vue";
import { IPA_TOURNAMENT_PAYBILL } from "@/routes/tournament/constants";
import {
  buildSchedulingCapacityGuide,
  FIRST_MATCH_AFTER_ARRIVAL_MINUTES,
  FIXTURE_GAP_MINUTES,
} from "@/routes/tournament/schedulingCapacity";

const TOTAL_STEPS = 6;
const router = useRouter();
const authStore = useAuthStore();

const step = ref(1);
const submitting = ref(false);
const posterPreview = ref("");
const posterFile = ref(null);
const facilityBrowseOpen = ref(false);
const activeBookingSlot = ref(0);
const facilityBookings = ref([null]);

const steps = [
  { id: 1, label: "Details", icon: "fa-solid fa-futbol" },
  { id: 2, label: "Schedule", icon: "fa-solid fa-calendar-days" },
  { id: 3, label: "Officials", icon: "fa-solid fa-user-shield" },
  { id: 4, label: "Services", icon: "fa-solid fa-briefcase" },
  { id: 5, label: "Prizes", icon: "fa-solid fa-coins" },
  { id: 6, label: "Review", icon: "fa-solid fa-flag-checkered" },
];

const playerFormats = [
  { value: "6", label: "6 aside (5 infield + 1 keeper)" },
  { value: "7", label: "7 aside (6 infield + 1 keeper)" },
  { value: "11", label: "11 aside (10 infield + 1 keeper)" },
];

const ageBrackets = ["U12", "U15", "U17", "U20", "20+"];
const teamCategoryOptions = [
  "Academy",
  "Corporate",
  "Local Teams",
  "Professional Teams",
  "Preschool",
  "HighSchool",
  "Tertiary/College",
];
const gameTypes = ["Group Stages to Knock Out", "Knock Out Basis"];
const officialTypeOptions = ["Center Referee", "Assistant Referee", "Fourth Official", "Match Commissioner"];
const awardModes = ["Trophy", "Cash Prize"];

const form = reactive({
  participantTeams: 8,
  playerFormat: "11",
  ageBracket: "U20",
  teamCategory: "",
  gameType: gameTypes[0],
  dateMode: "single",
  singleDate: "",
  startDate: "",
  endDate: "",
  venueFieldMode: "single",
  fieldSlotCount: 1,
  pinSlots: [""],
  matchDurationMinutes: 30,
  teamArrivalTime: "",
  prizeContributionModel: "host",
  hostPrizeContribution: 0,
  teamContributionTotal: 0,
  officialTypes: [],
  officialSlots: [],
  officialAssignmentMode: "platform",
  privateOfficialNotes: "",
  officialsBudget: 0,
  serviceProviders: {
    sound: { enabled: false, slots: 1 },
    food: { enabled: false, slots: 0 },
    emergency: { enabled: false, slots: 1 },
  },
  soundBudget: 0,
  emergencyBudget: 0,
  awardModes: ["Cash Prize"],
  awardSplit: "first-second",
  firstPlaceAward: 0,
  secondPlaceAward: 0,
  ipaFinanceTrustEnabled: false,
  paymentCollectionMode: "ipa_paybill",
  hostPaymentProvider: "",
  hostPaymentDestination: "",
});

const venueSlotCount = computed(() =>
  form.venueFieldMode === "single" ? 1 : form.fieldSlotCount
);

const suggestedVenueDate = computed(() =>
  form.dateMode === "single" ? form.singleDate : form.startDate
);

const venueBookingDurationMinutes = computed(() =>
  Math.max(Number(form.matchDurationMinutes) || 15, 60)
);

const dateRangeInvalid = computed(
  () =>
    form.dateMode === "multiple" &&
    form.startDate &&
    form.endDate &&
    form.endDate < form.startDate
);

const schedulingCapacityGuide = computed(() =>
  buildSchedulingCapacityGuide({
    participantTeams: form.participantTeams,
    gameType: form.gameType,
    dateMode: form.dateMode,
    singleDate: form.singleDate,
    startDate: form.startDate,
    endDate: form.endDate,
    venueFieldMode: form.venueFieldMode,
    fieldSlotCount: form.fieldSlotCount,
    teamArrivalTime: form.teamArrivalTime,
    matchDurationMinutes: form.matchDurationMinutes,
  })
);

const schedulingCapacityIcon = computed(() => {
  const map = {
    ready: "fa-solid fa-circle-check",
    tight: "fa-solid fa-triangle-exclamation",
    insufficient: "fa-solid fa-circle-xmark",
    incomplete: "fa-solid fa-circle-info",
  };
  return map[schedulingCapacityGuide.value.status] || map.incomplete;
});

watch(
  () => [form.venueFieldMode, form.fieldSlotCount],
  () => {
    if (form.venueFieldMode === "single") {
      form.fieldSlotCount = 1;
    } else if (form.fieldSlotCount < 2) {
      form.fieldSlotCount = 2;
    }
    syncVenueSlots();
  },
  { immediate: true }
);

function syncVenueSlots() {
  const count = venueSlotCount.value;
  while (form.pinSlots.length < count) form.pinSlots.push("");
  form.pinSlots.length = count;
  while (facilityBookings.value.length < count) facilityBookings.value.push(null);
  facilityBookings.value.length = count;
}

function onVenueModeChange() {
  if (form.venueFieldMode === "single") {
    form.fieldSlotCount = 1;
  } else if (form.fieldSlotCount < 2) {
    form.fieldSlotCount = 2;
  }
  syncVenueSlots();
}

function onStartDateChange() {
  if (form.endDate && form.startDate && form.endDate < form.startDate) {
    form.endDate = form.startDate;
  }
}

const progressPercent = computed(() => (step.value / TOTAL_STEPS) * 100);

const hostName = computed(() => {
  const u = authStore.user || {};
  return (
    u.organization_name ||
    u.team_name ||
    u.full_name ||
    u.name ||
    u.username ||
    u.email ||
    "Logged-in IPA customer"
  );
});

const selectedPlayerFormatLabel = computed(() => {
  const found = playerFormats.find((f) => f.value === form.playerFormat);
  return found ? found.label : "";
});

const totalPrizePool = computed(() =>
  form.prizeContributionModel === "host"
    ? Number(form.hostPrizeContribution || 0)
    : Number(form.teamContributionTotal || 0)
);

const perTeamContribution = computed(() => {
  if (form.prizeContributionModel !== "teams") return 0;
  const teams = Number(form.participantTeams || 0);
  if (teams <= 0) return 0;
  return Math.ceil(totalPrizePool.value / teams);
});

const totalOfficialSlots = computed(() =>
  form.officialSlots.reduce((sum, item) => sum + Number(item.count || 0), 0)
);

const operationalBudget = computed(() => {
  const soundCost = form.serviceProviders.sound.enabled ? Number(form.soundBudget || 0) : 0;
  const emergencyCost = form.serviceProviders.emergency.enabled ? Number(form.emergencyBudget || 0) : 0;
  return Number(form.officialsBudget || 0) + soundCost + emergencyCost;
});

const availableAwardBudget = computed(() => Math.max(0, totalPrizePool.value - operationalBudget.value));

const awardOverBudget = computed(() => {
  const second = form.awardSplit === "first-second" ? Number(form.secondPlaceAward || 0) : 0;
  return Number(form.firstPlaceAward || 0) + second > availableAwardBudget.value;
});

const dateSummary = computed(() =>
  form.dateMode === "single" ? form.singleDate || "Not set" : `${form.startDate || "?"} to ${form.endDate || "?"}`
);

const venueSummary = computed(() => {
  const mode = form.venueFieldMode === "multiple" ? `${venueSlotCount.value} fields` : "Single field";
  const booked = facilityBookings.value.filter(Boolean).length;
  const pins = form.pinSlots.filter((p) => p?.trim()).length;
  return `${mode} · ${pins} pin(s) · ${booked} booked facility(s)`;
});

const paymentSummary = computed(() => {
  if (form.paymentCollectionMode === "ipa_paybill") {
    return `Paybill ${IPA_TOURNAMENT_PAYBILL} · account = each team name`;
  }
  const provider =
    form.hostPaymentProvider === "airtel_money" ? "Airtel Money" : "M-Pesa";
  const dest = form.hostPaymentDestination?.trim() || "—";
  return `${provider} · ${dest}`;
});

function onPaymentModeChange() {
  if (form.paymentCollectionMode === "ipa_paybill") {
    form.hostPaymentProvider = "";
    form.hostPaymentDestination = "";
  }
}

const step1Progress = computed(() => {
  let p = 0;
  if (hostName.value) p += 20;
  if (Number(form.participantTeams) >= 2) p += 20;
  if (form.ageBracket) p += 20;
  if (form.playerFormat) p += 20;
  if (form.teamCategory) p += 20;
  return Math.min(p, 100);
});

function openFacilityBrowse() {
  const count = venueSlotCount.value;
  const next = facilityBookings.value.findIndex((b, i) => i < count && !b);
  if (form.venueFieldMode === "multiple" && next < 0) {
    toastError("All field slots already have registered facility bookings.");
    return;
  }
  activeBookingSlot.value = form.venueFieldMode === "single" ? 0 : next;
  facilityBrowseOpen.value = true;
}

function onFacilityBooked(payload) {
  facilityBookings.value[activeBookingSlot.value] = payload;
  if (payload?.court?.pin_location) {
    form.pinSlots[activeBookingSlot.value] = payload.court.pin_location;
  }
  facilityBrowseOpen.value = false;
}

function clearFacilityBooking(index) {
  facilityBookings.value[index] = null;
  activeBookingSlot.value = index;
  facilityBrowseOpen.value = true;
}

function goNext() {
  if (step.value < TOTAL_STEPS) step.value += 1;
}

function goBack() {
  if (step.value > 1) step.value -= 1;
}

function toastError(message) {
  dispatchAppToast("error", "Tournament setup", message);
}

function validateStep1() {
  const teams = Number(form.participantTeams);
  if (teams < 8) {
    toastError("Enter at least 8 teams (including host).");
    return;
  }
  if (teams % 2 !== 0) {
    toastError("Team count must be an even number.");
    return;
  }
  if (!form.teamCategory) {
    toastError("Select a team category.");
    return;
  }
  goNext();
}

function validateStep2() {
  if (form.dateMode === "single" && !form.singleDate) {
    toastError("Choose the match day.");
    return;
  }
  if (form.dateMode === "multiple" && (!form.startDate || !form.endDate)) {
    toastError("Choose start and end dates.");
    return;
  }
  if (dateRangeInvalid.value) {
    toastError("End date cannot be before the start date.");
    return;
  }
  if (!form.teamArrivalTime) {
    toastError("Set team arrival time.");
    return;
  }
  if (Number(form.matchDurationMinutes) < 15) {
    toastError("Match duration must be at least 15 minutes.");
    return;
  }
  if (!schedulingCapacityGuide.value.canProceed) {
    toastError(
      schedulingCapacityGuide.value.headline ||
        "Adjust tournament days, fields, arrival time, or match duration so all fixtures can fit.",
    );
    return;
  }
  const count = venueSlotCount.value;
  for (let i = 0; i < count; i += 1) {
    const hasPin = form.pinSlots[i]?.trim();
    const hasBooking = facilityBookings.value[i];
    if (!hasPin && !hasBooking) {
      toastError(`Add a pin location or book a facility for field ${i + 1}.`);
      return;
    }
  }
  goNext();
}

function validateStep3() {
  if (!form.officialTypes.length) {
    toastError("Select at least one official type.");
    return;
  }
  if (form.officialAssignmentMode === "private" && !form.privateOfficialNotes.trim()) {
    toastError("Provide private official details.");
    return;
  }
  goNext();
}

function validateStep4() {
  goNext();
}

function validateStep5() {
  if (form.prizeContributionModel === "host" && Number(form.hostPrizeContribution) <= 0) {
    toastError("Enter host prize contribution amount.");
    return;
  }
  if (form.prizeContributionModel === "teams" && Number(form.teamContributionTotal) <= 0) {
    toastError("Enter total team contribution target.");
    return;
  }
  if (!form.awardModes.length) {
    toastError("Select at least one award type.");
    return;
  }
  if (awardOverBudget.value) {
    toastError("Award amounts exceed available budget after service providers and officials.");
    return;
  }
  if (form.paymentCollectionMode === "host_direct") {
    if (!form.hostPaymentProvider) {
      toastError("Select M-Pesa or Airtel Money for receiving contributions.");
      return;
    }
    if (!form.hostPaymentDestination.trim()) {
      toastError("Enter your phone or Paybill number for deposits.");
      return;
    }
  }
  goNext();
}

function toggleMulti(arr, value) {
  const idx = arr.indexOf(value);
  if (idx >= 0) arr.splice(idx, 1);
  else arr.push(value);
}

function toggleOfficialType(type) {
  const idx = form.officialTypes.indexOf(type);
  if (idx >= 0) {
    form.officialTypes.splice(idx, 1);
    const slotIdx = form.officialSlots.findIndex((s) => s.type === type);
    if (slotIdx >= 0) form.officialSlots.splice(slotIdx, 1);
  } else {
    form.officialTypes.push(type);
    form.officialSlots.push({ type, count: 1 });
  }
}

function currency(value) {
  return `KSH ${Number(value || 0).toLocaleString()}`;
}

function onPosterChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  posterFile.value = file;
  posterPreview.value = URL.createObjectURL(file);
}

async function parseApiError(response) {
  const text = await response.text();
  try {
    const body = text ? JSON.parse(text) : null;
    const detail = body?.detail;
    if (typeof detail === "string") return detail;
    if (detail?.message) return detail.message;
    if (Array.isArray(detail)) {
      return detail.map((item) => item.msg || item.message || String(item)).filter(Boolean).join(" ");
    }
    return body?.message || text || response.statusText;
  } catch {
    return text || response.statusText;
  }
}

async function submitRegistration() {
  if (!posterFile.value) {
    toastError("Upload a tournament poster for the fanbase.");
    return;
  }
  if (awardOverBudget.value) {
    toastError("Fix award amounts before submitting.");
    return;
  }

  const token = authStore.accessToken;
  if (!token) {
    toastError("Please log in to create a tournament.");
    router.push({ name: "CustomerSignIn" });
    return;
  }

  submitting.value = true;
  try {
    const registrationPayload = {
      ...form,
      serviceProviders: {
        ...form.serviceProviders,
        food: {
          enabled: form.serviceProviders.food.enabled,
          slots: form.serviceProviders.food.enabled ? 0 : 0,
        },
      },
      fieldSlotCount: form.venueFieldMode === "single" ? 1 : form.fieldSlotCount,
      hostPaymentProvider:
        form.paymentCollectionMode === "host_direct" ? form.hostPaymentProvider || null : null,
      hostPaymentDestination:
        form.paymentCollectionMode === "host_direct" ? form.hostPaymentDestination : "",
      facilityBookings: facilityBookings.value,
    };

    const formData = new FormData();
    formData.append("registration", JSON.stringify(registrationPayload));
    formData.append("poster", posterFile.value);

    const response = await fetch(buildApiUrl("/api/tournaments/register"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const message = await parseApiError(response);
      throw new Error(message || `Registration failed (${response.status})`);
    }

    const data = await response.json();
    dispatchAppToast(
      "success",
      "Tournament published",
      data.message ||
        "Your tournament is live on the fanbase. Football teams can register now."
    );
    router.push({
      name: "TournamentHostDashboardDetail",
      params: { tournamentNumber: String(data.tournament_number) },
    });
  } catch (err) {
    toastError(err.message || "Could not save tournament registration. Please try again.");
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap");

.upload-container {
  display: flex;
  flex-direction: column;
  background: var(--background-color1);
  padding-top: 80px;
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  animation: fadeIn 0.4s ease-in-out;
  min-height: 100vh;
  box-sizing: border-box;
  font-family: "Poppins", sans-serif;
  color: var(--text-color1);
}

.upload-footer-wrap {
  margin-top: auto;
}

.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto 12px;
  max-width: 1000px;
  width: calc(100% - 30px);
  color: var(--border-color-hover);
  text-decoration: none;
  font-weight: 600;
}

.page-title {
  text-align: center;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: var(--text-color1);
}

.page-title i {
  color: var(--border-color-hover);
}

.upload-card {
  max-width: 1000px;
  width: calc(100% - 30px);
  margin: 0 auto 40px;
  background: var(--background-color2);
  padding: 40px;
  border-radius: 26px;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.fadeIn {
  animation: fadeIn 0.45s ease both;
}

.steps-wrapper {
  margin-bottom: 40px;
}

.progress-line {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 24px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--border-color-hover), #00b4ff);
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 10px;
}

.steps {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.step {
  text-align: center;
  opacity: 0.6;
  transition: all 0.3s ease;
  font-size: 14px;
  flex: 1;
}

.step.active {
  opacity: 1;
  transform: scale(1.05);
}

.step.completed {
  opacity: 0.9;
}

.step i {
  display: block;
  font-size: 20px;
  margin-bottom: 8px;
  color: var(--border-color-hover);
}

.step.active i {
  color: #00b4ff;
  transform: scale(1.15);
}

.step-number {
  color: var(--border-color-hover);
  font-weight: 600;
  font-size: 12px;
}

.step-label {
  font-size: 11px;
  margin-top: 4px;
  color: #94a3b8;
}

.step-header {
  margin-bottom: 32px;
  text-align: center;
}

.title {
  font-weight: 700;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
  color: var(--text-color1);
}

.step-description {
  color: var(--border-color-hover);
  font-size: 15px;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 32px;
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.full-width-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group--full {
  grid-column: 1 / -1;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
  color: var(--text-color1);
}

.form-group label i {
  color: var(--border-color-hover);
  width: 20px;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 15px;
  color: var(--text-color1);
  transition: all 0.3s ease;
  font-family: inherit;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: var(--border-color-hover);
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 185, 255, 0.1);
}

.readonly-input {
  opacity: 0.85;
  cursor: not-allowed;
}

.input-hint {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

.field-error {
  margin: 0;
  font-size: 13px;
  color: #ef4444;
  font-weight: 600;
}

.input-invalid {
  border-color: #ef4444 !important;
}

.schedule-capacity-guide {
  margin: 1.5rem 0 1.75rem;
  padding: 1.25rem 1.35rem;
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
}

.schedule-capacity-guide--ready {
  border-color: color-mix(in srgb, #43a047 35%, rgba(255, 255, 255, 0.12));
  background: color-mix(in srgb, #43a047 8%, rgba(255, 255, 255, 0.04));
}

.schedule-capacity-guide--tight {
  border-color: color-mix(in srgb, #f59e0b 40%, rgba(255, 255, 255, 0.12));
  background: color-mix(in srgb, #f59e0b 8%, rgba(255, 255, 255, 0.04));
}

.schedule-capacity-guide--insufficient {
  border-color: color-mix(in srgb, #ef4444 40%, rgba(255, 255, 255, 0.12));
  background: color-mix(in srgb, #ef4444 8%, rgba(255, 255, 255, 0.04));
}

.schedule-capacity-guide__head {
  margin-bottom: 1rem;
}

.schedule-capacity-guide__title {
  margin: 0 0 0.4rem;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.schedule-capacity-guide__lead {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #94a3b8;
}

.schedule-capacity-guide__headline {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  font-size: 0.92rem;
  line-height: 1.5;
  font-weight: 600;
}

.schedule-capacity-guide__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.schedule-capacity-stat {
  padding: 0.75rem 0.85rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.schedule-capacity-stat--highlight {
  border-color: color-mix(in srgb, #2563eb 30%, rgba(255, 255, 255, 0.08));
  background: color-mix(in srgb, #2563eb 10%, rgba(255, 255, 255, 0.05));
}

.schedule-capacity-stat__label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.schedule-capacity-stat strong {
  font-size: 1rem;
  color: #f8fafc;
}

.schedule-capacity-guide__rules,
.schedule-capacity-guide__suggestions {
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.86rem;
  line-height: 1.55;
  color: #cbd5e1;
}

.schedule-capacity-guide__rules {
  margin-bottom: 0.85rem;
}

.schedule-capacity-guide__rules li,
.schedule-capacity-guide__suggestions li {
  margin-bottom: 0.35rem;
}

.schedule-capacity-guide__rules i {
  margin-right: 0.35rem;
  opacity: 0.85;
}

.schedule-capacity-guide__suggestions {
  padding: 0.85rem 1rem 0.85rem 2rem;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.15);
  list-style: disc;
}

.venue-fields-block {
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.25rem;
}

.venue-fields-title {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.venue-fields-lead {
  margin-bottom: 1rem !important;
}

.venue-slot + .venue-slot {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed rgba(255, 255, 255, 0.12);
}

.book-facility-btn {
  margin-top: 1rem;
  width: 100%;
  padding: 14px 20px;
  border: none;
  border-radius: 999px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.book-facility-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 185, 255, 0.3);
}

.booked-facility {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
  font-size: 14px;
}

.booked-facility i {
  color: #10b981;
}

.link-btn {
  margin-left: auto;
  border: none;
  background: transparent;
  color: var(--border-color-hover);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

.facility-picker-silent :deep(.ncp-trigger),
.facility-picker-silent :deep(.ncp-booked) {
  display: none;
}

.budget-summary {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.budget-lines {
  list-style: none;
  margin: 0;
  padding: 0;
}

.budget-lines li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.budget-lines__remain {
  font-weight: 700;
  color: var(--border-color-hover);
}

.chip-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.chip {
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-color1);
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.25s ease;
  font-size: 14px;
}

.chip.active {
  border-color: var(--border-color-hover);
  background: rgba(0, 185, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 185, 255, 0.2);
  transform: translateY(-2px);
}

.instruction-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;
}

.instruction-card i {
  font-size: 22px;
  color: var(--border-color-hover);
  margin-top: 2px;
}

.instruction-card h4 {
  margin: 0 0 4px;
  font-size: 15px;
}

.instruction-card p {
  margin: 0;
  font-size: 14px;
  color: #94a3b8;
  line-height: 1.5;
}

.upload-instructions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.pricing-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

.pricing-section-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  color: var(--border-color-hover);
  font-size: 16px;
}

.earnings-breakdown {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 20px;
}

.slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.toggle-row {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.food-open-market-row {
  align-items: flex-start;
}

.food-open-market-note {
  flex: 1 1 100%;
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text-color2);
}

.inline-alert {
  margin-top: 12px;
  color: #f59e0b;
  font-weight: 600;
  display: flex;
  gap: 8px;
  align-items: center;
}

.poster-preview {
  margin: 16px 0 24px;
  text-align: center;
}

.poster-preview img {
  max-width: min(100%, 480px);
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.review-panel {
  background: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
}

.review-list {
  margin: 0;
  padding-left: 1.2rem;
  line-height: 1.7;
}

.rule-note {
  margin: 16px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--border-color-hover);
}

.step-navigation {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-progress {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-text {
  display: block;
  text-align: center;
  font-size: 14px;
  color: #94a3b8;
}

.nav-buttons {
  display: flex;
  gap: 16px;
  margin-top: 32px;
}

.next-btn,
.nav-btn {
  padding: 16px 32px;
  min-height: 48px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.next-btn,
.nav-btn.primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.nav-btn.secondary {
  background: transparent;
  color: var(--text-color1);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.next-btn:hover,
.nav-btn.primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 185, 255, 0.3);
}

.nav-btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.nav-buttons .nav-btn.primary,
.nav-buttons .next-btn {
  flex: 1;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.35s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(14px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@media (max-width: 768px) {
  .upload-card {
    padding: 25px;
    border-radius: 20px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .steps .step-label {
    font-size: 10px;
  }

  .step i {
    font-size: 16px;
  }

  .nav-buttons {
    flex-direction: column;
  }

  .nav-buttons .nav-btn {
    width: 100%;
  }

  .title {
    font-size: 20px;
  }
}
</style>
